import { Link } from 'react-router-dom';
import { useTranslation } from '@dukanti/shared';
import { StockThumbnail, type StockTableProps } from './stock-table';
import { StockStatusBadge } from './stock-status-badge';

export type StockMobileCardsProps = StockTableProps;

export function StockMobileCards({
  rows,
  returnTo,
  onViewProduct,
}: StockMobileCardsProps): React.JSX.Element {
  const { t, dir, lang } = useTranslation();
  return (
    <ul
      dir={dir}
      data-testid="stock-mobile-cards"
      className="flex min-w-0 flex-col gap-3 md:hidden"
    >
      {rows.map((row) => (
        <li
          key={row.variantId}
          data-testid={`stock-mobile-card-${row.variantId}`}
          data-variant-id={row.variantId}
          data-product-id={row.productId}
          className="border-border bg-card shadow-admin-card min-w-0 rounded-xl border p-4"
        >
          <div className="flex min-w-0 items-start gap-3">
            <StockThumbnail url={row.primaryImageUrl} />
            <div className="min-w-0 flex-1 space-y-1 [overflow-wrap:anywhere]">
              <p dir="auto" className="text-foreground text-sm font-semibold">
                {row.productName}
              </p>
              <p dir="auto" className="text-muted-foreground text-sm">
                {row.optionSummary || t('admin.stock.worklist.defaultVariant')}
              </p>
              {row.barcode ? (
                <p className="text-muted-foreground text-xs">
                  {t('admin.stock.worklist.barcode')}: <bdi dir="ltr">{row.barcode}</bdi>
                </p>
              ) : null}
              <div className="flex flex-wrap gap-1.5 text-xs">
                {!row.active ? (
                  <span className="bg-admin-surface-muted text-muted-foreground rounded px-2 py-1">
                    {t('admin.stock.worklist.inactive')}
                  </span>
                ) : null}
                {!row.productPublished ? (
                  <span className="bg-admin-surface-muted text-muted-foreground rounded px-2 py-1">
                    {t('admin.stock.worklist.unpublished')}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between gap-3">
            <div>
              <p className="text-muted-foreground text-xs">{t('admin.stock.worklist.available')}</p>
              <p
                data-testid="stock-available"
                className="text-foreground text-2xl font-semibold tabular-nums"
              >
                {new Intl.NumberFormat(lang).format(row.availableUnits)}
              </p>
            </div>
            <StockStatusBadge status={row.status} />
          </div>
          <Link
            to={`/products/${row.productId}`}
            state={{ stockReturnTo: returnTo }}
            onClick={() => onViewProduct(row.variantId)}
            aria-label={t('admin.stock.worklist.viewProductLabel', {
              product: row.productName,
              variant: row.optionSummary || t('admin.stock.worklist.defaultVariant'),
              // React escapes this attribute; keep the merchant identity literal.
              interpolation: { escapeValue: false },
            })}
            className="border-input text-foreground hover:bg-admin-surface-muted focus-visible:ring-ring mt-3 flex min-h-11 items-center justify-center rounded-md border px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2"
          >
            {t('admin.stock.worklist.viewProduct')}
          </Link>
        </li>
      ))}
    </ul>
  );
}
